import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { BOOKING } from "@/constants/testIds";
import { BOOKING_CONDITIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm() {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [condition, setCondition] = useState("");
  const [gender, setGender] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [slots, setSlots] = useState([]); // [{ time, booked }]
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "+91 ",
    age: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Whenever the user picks a date, load that date's slot availability so the
  // dropdown only shows times that are still open.
  useEffect(() => {
    if (!date) {
      setSlots([]);
      setTime("");
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    const dateStr = format(date, "yyyy-MM-dd");
    axios
      .get(`${API}/appointments/slots`, { params: { date: dateStr } })
      .then((r) => {
        if (cancelled) return;
        setSlots(r.data?.slots || []);
      })
      .catch(() => {
        if (cancelled) return;
        setSlots([]);
      })
      .finally(() => !cancelled && setSlotsLoading(false));
    // Reset previously selected time when date changes.
    setTime("");
    return () => {
      cancelled = true;
    };
  }, [date]);

  const openSlots = slots.filter((s) => !s.booked);
  const allBooked = slots.length > 0 && openSlots.length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !date || !time) {
      toast.error("Please fill name, phone, date and pick a time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        age: form.age ? Number(form.age) : null,
        gender: gender || null,
        condition: condition || null,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/appointments`, payload);
      setSuccess(true);
      setForm({ name: "", phone: "+91 ", age: "", message: "" });
      setDate(null);
      setTime("");
      setCondition("");
      setGender("");
      setSlots([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form
        data-testid={BOOKING.form}
        onSubmit={handleSubmit}
        className="bg-white text-slate-900 rounded-3xl border border-slate-200 shadow-sm p-8 md:p-10 grid md:grid-cols-2 gap-6"
      >
        <div className="md:col-span-2">
          <h3 className="font-serif-display text-3xl md:text-4xl text-slate-900">
            Book your consultation
          </h3>
          <p className="text-slate-600 mt-2 text-sm md:text-base">
            Pick any open slot below — your booking is reserved instantly.
            Your first visit takes a full{" "}
            <span className="text-slate-900 font-medium">20 minutes</span> —
            unhurried and thorough.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor={BOOKING.name}>Full name *</Label>
          <Input
            id={BOOKING.name}
            data-testid={BOOKING.name}
            value={form.name}
            onChange={update("name")}
            placeholder="e.g., Anjali Patil"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={BOOKING.phone}>Phone *</Label>
          <Input
            id={BOOKING.phone}
            data-testid={BOOKING.phone}
            value={form.phone}
            onChange={update("phone")}
            placeholder="e.g., +91 98765 43210"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={BOOKING.age}>Age</Label>
          <Input
            id={BOOKING.age}
            data-testid={BOOKING.age}
            type="number"
            min="0"
            max="130"
            value={form.age}
            onChange={update("age")}
            placeholder="e.g., 45"
          />
        </div>

        <div className="space-y-2">
          <Label>Gender</Label>
          <Select value={gender} onValueChange={setGender}>
            <SelectTrigger data-testid="booking-gender">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Others">Others</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Concern</Label>
          <Select value={condition} onValueChange={setCondition}>
            <SelectTrigger data-testid={BOOKING.condition}>
              <SelectValue placeholder="Select a concern" />
            </SelectTrigger>
            <SelectContent>
              {BOOKING_CONDITIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Preferred date *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid={BOOKING.date}
                className="w-full justify-start text-left font-normal h-11 rounded-lg"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span className="text-slate-500">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  // Disable past dates AND Sundays (clinic holiday).
                  return d < today || d.getDay() === 0;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Available time slot *</Label>
          <Select value={time} onValueChange={setTime} disabled={!date || slotsLoading || allBooked}>
            <SelectTrigger data-testid={BOOKING.time}>
              <SelectValue
                placeholder={
                  !date
                    ? "Pick a date first"
                    : slotsLoading
                    ? "Checking availability…"
                    : allBooked
                    ? "All slots booked — choose another date"
                    : "Select an open slot"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {openSlots.map((s) => (
                <SelectItem
                  key={s.time}
                  value={s.time}
                  data-testid={`slot-${s.time.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  {s.time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {date && !slotsLoading && slots.length > 0 ? (
            <p className="text-xs text-slate-500">
              {allBooked
                ? "All slots are taken for this date. Try another day."
                : `${openSlots.length} of ${slots.length} slots open · 5:30–6:30 PM`}
            </p>
          ) : null}
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor={BOOKING.message}>Anything we should know?</Label>
          <Textarea
            id={BOOKING.message}
            data-testid={BOOKING.message}
            value={form.message}
            onChange={update("message")}
            placeholder="Existing conditions, medications, recent reports…"
            rows={4}
          />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            We only use your details to reserve your slot.
          </p>
          <Button
            type="submit"
            data-testid={BOOKING.submit}
            disabled={submitting}
            className="rounded-full px-8 h-12 bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Booking…
              </>
            ) : (
              "Confirm Appointment"
            )}
          </Button>
        </div>
      </form>

      <Dialog open={success} onOpenChange={setSuccess}>
        <DialogContent data-testid={BOOKING.successDialog}>
          <DialogHeader>
            <div className="mx-auto bg-[hsl(var(--primary))]/20 rounded-full p-3 w-fit mb-3">
              <CheckCircle2 className="h-8 w-8 text-slate-900" />
            </div>
            <DialogTitle className="text-center font-serif-display text-3xl">
              Appointment confirmed
            </DialogTitle>
            <DialogDescription className="text-center text-base">
              Your slot is reserved. Please arrive 10 minutes before the time with
              any past reports or prescriptions you have.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              data-testid={BOOKING.successClose}
              onClick={() => setSuccess(false)}
              className="rounded-full bg-[hsl(var(--primary-bold))] text-slate-900 hover:bg-[hsl(var(--primary-bold))]/90"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
